<template>
  <div class="product-form">
    <div class="card">
      <div class="header">
        <h2>{{ isEdit ? '✏️ Edit Product' : '➕ Add New Product' }}</h2>
        <router-link to="/products" class="btn btn-secondary">
          ← Back to Products
        </router-link>
      </div>
      
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ isEdit ? 'Loading product...' : 'Creating product...' }}</p>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="handleSubmit" class="product-form-content">
        <!-- Product Name Field (moved to top for better UX) -->
        <div class="form-group">
          <label for="name">Product Name *</label>
          <input
            id="name"
            v-model="form.name"
            type="text"
            :class="{ 'error': errors.name }"
            placeholder="Enter product name"
            required
            @blur="validateName"
            @input="onNameChange"
          />
          <div v-if="errors.name" class="error-message">
            {{ errors.name }}
          </div>
          <small class="field-help">Clear, descriptive product name</small>
        </div>

        <!-- SKU Field with Auto-generation -->
        <div class="form-group">
          <label for="sku">SKU *</label>
          <SkuInput
            v-model="form.sku"
            :product-name="form.name"
            :user-id="currentUserId"
            :error="errors.sku"
            :disabled="isEdit"
            @generated="onSkuGenerated"
            @validation-error="onSkuValidationError"
          />
          <div v-if="errors.sku" class="error-message">
            {{ errors.sku }}
          </div>
          <small class="field-help">
            {{ isEdit ? 'SKU cannot be changed after creation' : 'Auto-generated from product name or enter manually' }}
          </small>
        </div>

        <!-- Barcode Field (now optional) -->
        <div class="form-group">
          <label for="barcode">Barcode (Optional)</label>
          <div class="barcode-input-group">
            <input
              id="barcode"
              v-model="form.barcode"
              type="text"
              :class="{ 'error': errors.barcode }"
              placeholder="Enter product barcode or scan (optional)"
              @blur="validateBarcode"
              @input="onBarcodeChange"
            />
            <button
              type="button"
              class="btn-scan-barcode"
              @click="toggleBarcodeScanner"
              :disabled="scannerActive"
            >
              📷 {{ scannerActive ? 'Scanning...' : 'Scan' }}
            </button>
          </div>
          
          <!-- Global Product Match Info -->
          <div v-if="globalProductMatch" class="global-product-match">
            <div class="match-info">
              <h4>📦 Global Product Found</h4>
              <p><strong>Name:</strong> {{ globalProductMatch.name }}</p>
              <p><strong>Status:</strong> 
                <span :class="'status-' + globalProductMatch.status.toLowerCase()">
                  {{ globalProductMatch.status }}
                </span>
              </p>
              <button 
                type="button" 
                class="btn btn-outline btn-sm"
                @click="useGlobalProduct"
              >
                Use This Product
              </button>
            </div>
          </div>
          
          <!-- Barcode Scanner -->
          <div v-if="scannerActive" class="barcode-scanner-container">
            <div class="scanner-header">
              <h3>📷 Scan Product Barcode</h3>
              <button
                type="button"
                class="btn-close-scanner"
                @click="closeBarcodeScanner"
              >
                ✕
              </button>
            </div>
            
            <div class="scanner-content">
              <div v-if="scannerError" class="scanner-error">
                <p>{{ scannerError }}</p>
                <button
                  type="button"
                  class="btn btn-outline"
                  @click="initBarcodeScanner"
                >
                  Try Again
                </button>
              </div>
              
              <div v-else class="camera-viewport">
                <qrcode-stream
                  :paused="!scannerActive"
                  :formats="['qr_code', 'code_128', 'code_39', 'code_93', 'ean_13', 'ean_8']"
                  @detect="onBarcodeDetected"
                  @error="onScannerError"
                  @camera-on="onCameraReady"
                  class="barcode-reader"
                />
              </div>
              
              <div class="scanner-instructions">
                <p>📱 Point your camera at the barcode</p>
                <p>📏 Make sure the barcode is clear and well-lit</p>
                <p>🔍 The scanner will automatically detect and search for global products</p>
              </div>
            </div>
          </div>
          
          <div v-if="errors.barcode" class="error-message">
            {{ errors.barcode }}
          </div>
          <small class="field-help">Optional barcode - scans will search for existing global products</small>
        </div>

        <!-- Description Field -->
        <div class="form-group">
          <label for="description">Description</label>
          <textarea
            id="description"
            v-model="form.description"
            rows="3"
            :class="{ 'error': errors.description }"
            placeholder="Enter product description (optional)"
            @blur="validateDescription"
          ></textarea>
          <div v-if="errors.description" class="error-message">
            {{ errors.description }}
          </div>
          <small class="field-help">Additional product details</small>
        </div>

        <!-- Price Field -->
        <div class="form-group">
          <label for="price">Price *</label>
          <div class="input-group">
            <span class="input-prefix">$</span>
            <input
              id="price"
              v-model="form.price"
              type="number"
              step="0.01"
              min="0"
              :class="{ 'error': errors.price }"
              placeholder="0.00"
              required
              @blur="validatePrice"
            />
          </div>
          <div v-if="errors.price" class="error-message">
            {{ errors.price }}
          </div>
          <small class="field-help">Product price in USD</small>
        </div>

        <!-- Stock Quantity Field -->
        <div class="form-group">
          <label for="stockQuantity">Stock Quantity</label>
          <input
            id="stockQuantity"
            v-model="form.stockQuantity"
            type="number"
            min="0"
            :class="{ 'error': errors.stockQuantity }"
            placeholder="0"
            @blur="validateStockQuantity"
          />
          <div v-if="errors.stockQuantity" class="error-message">
            {{ errors.stockQuantity }}
          </div>
          <small class="field-help">Current stock level</small>
        </div>

        <!-- Error Summary -->
        <div v-if="submitError" class="error-summary">
          <h4>⚠️ Please fix the following errors:</h4>
          <p>{{ submitError }}</p>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="success-summary">
          <h4>✅ Success!</h4>
          <p>{{ successMessage }}</p>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <router-link to="/products" class="btn btn-secondary">
            Cancel
          </router-link>
          <button 
            v-if="isEdit" 
            type="button" 
            @click="resetForm"
            class="btn btn-outline"
            :disabled="!hasUnsavedChanges"
          >
            Reset
          </button>
          <button 
            type="submit" 
            class="btn btn-primary"
            :disabled="!isFormValid || submitting"
          >
            {{ submitting ? 'Processing...' : (isEdit ? 'Update Product' : 'Create Product') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { QrcodeStream } from 'vue-qrcode-reader'
import SkuInput from '@/components/SkuInput.vue'
import api from '@/services/api'

export default {
  name: 'ProductForm',
  components: {
    QrcodeStream,
    SkuInput
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    
    const isEdit = computed(() => !!route.params.id)
    const loading = ref(false)
    const submitting = ref(false)
    const submitError = ref('')
    const successMessage = ref('')
    const currentUserId = ref(null)
    const globalProductMatch = ref(null)

    // Form data
    const form = ref({
      barcode: '',
      sku: '',
      name: '',
      description: '',
      price: '',
      stockQuantity: 0
    })

    // Form errors
    const errors = ref({
      barcode: '',
      sku: '',
      name: '',
      description: '',
      price: '',
      stockQuantity: ''
    })

    // Barcode scanner state
    const scannerActive = ref(false)
    const scannerError = ref('')

    // Get current user ID
    const getCurrentUser = async () => {
      try {
        const response = await api.get('/auth/me')
        currentUserId.value = response.data.id
      } catch (error) {
        console.error('Error getting current user:', error)
      }
    }

    // Search for global products by barcode
    const searchGlobalProduct = async (barcode) => {
      if (!barcode || barcode.length < 3) {
        globalProductMatch.value = null
        return
      }

      try {
        const response = await api.get(`/search/global-products?barcode=${encodeURIComponent(barcode)}`)
        if (response.data.length > 0) {
          globalProductMatch.value = response.data[0]
        } else {
          globalProductMatch.value = null
        }
      } catch (error) {
        console.error('Error searching global products:', error)
        globalProductMatch.value = null
      }
    }

    // Use global product data
    const useGlobalProduct = () => {
      if (globalProductMatch.value) {
        form.value.name = globalProductMatch.value.name
        form.value.barcode = globalProductMatch.value.barcode
        
        // Clear name errors since we have a valid global product
        errors.value.name = ''
        errors.value.barcode = ''
      }
    }

    // Handle name changes
    const onNameChange = () => {
      // Clear previous errors
      errors.value.name = ''
      
      // The SkuInput component will handle auto-generation
    }

    // Handle barcode changes
    const onBarcodeChange = () => {
      // Clear previous errors
      errors.value.barcode = ''
      
      // Search for global products
      if (form.value.barcode) {
        searchGlobalProduct(form.value.barcode)
      } else {
        globalProductMatch.value = null
      }
    }

    // Handle SKU generation
    const onSkuGenerated = (sku) => {
      form.value.sku = sku
      errors.value.sku = ''
    }

    // Handle SKU validation errors
    const onSkuValidationError = (error) => {
      errors.value.sku = error
    }

    // Validation methods
    const validateBarcode = () => {
      // Barcode is now optional
      if (form.value.barcode && form.value.barcode.length < 3) {
        errors.value.barcode = 'Barcode must be at least 3 characters if provided'
        return false
      }
      errors.value.barcode = ''
      return true
    }

    const validateSku = () => {
      if (!form.value.sku || form.value.sku.trim() === '') {
        errors.value.sku = 'SKU is required'
        return false
      }
      if (form.value.sku.length < 3) {
        errors.value.sku = 'SKU must be at least 3 characters'
        return false
      }
      errors.value.sku = ''
      return true
    }

    const validateName = () => {
      if (!form.value.name.trim()) {
        errors.value.name = 'Product name is required'
        return false
      }
      if (form.value.name.length < 2) {
        errors.value.name = 'Product name must be at least 2 characters'
        return false
      }
      errors.value.name = ''
      return true
    }

    const validateDescription = () => {
      if (form.value.description && form.value.description.length > 500) {
        errors.value.description = 'Description must be less than 500 characters'
        return false
      }
      errors.value.description = ''
      return true
    }

    const validatePrice = () => {
      const price = parseFloat(form.value.price)
      if (!form.value.price || isNaN(price)) {
        errors.value.price = 'Price is required'
        return false
      }
      if (price < 0) {
        errors.value.price = 'Price must be a positive number'
        return false
      }
      if (price > 999999.99) {
        errors.value.price = 'Price cannot exceed $999,999.99'
        return false
      }
      errors.value.price = ''
      return true
    }

    const validateStockQuantity = () => {
      const stock = parseInt(form.value.stockQuantity)
      if (form.value.stockQuantity !== '' && (isNaN(stock) || stock < 0)) {
        errors.value.stockQuantity = 'Stock quantity must be a non-negative number'
        return false
      }
      errors.value.stockQuantity = ''
      return true
    }

    // Validate entire form
    const validateForm = () => {
      const isValid = [
        validateSku(),
        validateName(),
        validateBarcode(),
        validateDescription(),
        validatePrice(),
        validateStockQuantity()
      ].every(Boolean)
      
      return isValid
    }

    // Computed property for form validity
    const isFormValid = computed(() => {
      return form.value.sku.trim() && 
             form.value.name.trim() && 
             form.value.price &&
             !Object.values(errors.value).some(error => error)
    })

    // Load product data for edit mode
    const loadProduct = async (id) => {
      try {
        loading.value = true
        submitError.value = ''
        
        const response = await api.get(`/products/${id}`)
        const product = response.data
        
        // Check if product exists
        if (!product) {
          submitError.value = 'Product not found'
          return
        }
        
        form.value = {
          barcode: product.barcode || '',
          sku: product.sku || '',
          name: product.name || '',
          description: product.description || '',
          price: product.price || '',
          stockQuantity: product.stockQuantity || 0
        }
        
        // Store original form data for comparison
        originalFormData.value = { ...form.value }
        
        // Check for global product match if barcode exists
        if (form.value.barcode) {
          searchGlobalProduct(form.value.barcode)
        }
        
      } catch (error) {
        console.error('Error loading product:', error)
        if (error.response?.status === 404) {
          submitError.value = 'Product not found. It may have been deleted.'
        } else {
          submitError.value = error.response?.data?.message || 'Failed to load product data'
        }
      } finally {
        loading.value = false
      }
    }

    // Track form changes
    const originalFormData = ref({})
    const hasUnsavedChanges = computed(() => {
      if (!isEdit.value) return false
      return JSON.stringify(form.value) !== JSON.stringify(originalFormData.value)
    })

    // Submit form
    const handleSubmit = async () => {
      if (!validateForm()) {
        submitError.value = 'Please fix the validation errors above'
        return
      }

      try {
        submitting.value = true
        submitError.value = ''
        successMessage.value = ''

        const formData = {
          name: form.value.name.trim(),
          sku: form.value.sku.trim(),
          barcode: form.value.barcode.trim() || null,
          description: form.value.description.trim() || null,
          price: parseFloat(parseFloat(form.value.price).toFixed(2)),
          stockQuantity: parseInt(form.value.stockQuantity) || 0
        }

        if (isEdit.value) {
          await api.put(`/products/${route.params.id}`, formData)
          successMessage.value = 'Product updated successfully!'
        } else {
          await api.post('/products', formData)
          successMessage.value = 'Product created successfully!'
        }

        // Redirect to products list after short delay
        setTimeout(() => {
          router.push('/products')
        }, 1500)

      } catch (error) {
        console.error('Error submitting form:', error)
        submitError.value = error.response?.data?.message || 'Failed to save product'
      } finally {
        submitting.value = false
      }
    }

    // Reset form
    const resetForm = () => {
      form.value = { ...originalFormData.value }
      globalProductMatch.value = null
      if (form.value.barcode) {
        searchGlobalProduct(form.value.barcode)
      }
    }

    // Barcode scanner methods
    const toggleBarcodeScanner = () => {
      if (scannerActive.value) {
        closeBarcodeScanner()
      } else {
        initBarcodeScanner()
      }
    }

    const initBarcodeScanner = () => {
      scannerActive.value = true
      scannerError.value = ''
      console.log('🔍 Barcode scanner activated for product form')
    }

    const closeBarcodeScanner = () => {
      scannerActive.value = false
      scannerError.value = ''
      console.log('❌ Barcode scanner closed')
    }

    const onBarcodeDetected = (detectedCodes) => {
      console.log('📊 Barcode detected in product form:', detectedCodes)
      
      if (detectedCodes.length > 0) {
        const barcode = detectedCodes[0].rawValue
        console.log('✅ Setting barcode:', barcode)
        
        // Set the barcode in the form
        form.value.barcode = barcode
        
        // Close the scanner
        closeBarcodeScanner()
        
        // Clear any existing barcode errors
        errors.value.barcode = ''
        
        // Search for global products
        searchGlobalProduct(barcode)
        
        // Focus on the name field if it's empty
        setTimeout(() => {
          if (!form.value.name.trim()) {
            const nameField = document.getElementById('name')
            if (nameField) {
              nameField.focus()
            }
          }
        }, 100)
      }
    }

    const onScannerError = (error) => {
      console.error('❌ Scanner error:', error)
      scannerError.value = error.name === 'NotAllowedError' 
        ? 'Camera access denied. Please enable camera permissions.'
        : 'Camera error. Please try again.'
    }

    const onCameraReady = () => {
      console.log('📷 Camera ready for barcode scanning')
      scannerError.value = ''
    }

    // Lifecycle
    onMounted(() => {
      getCurrentUser()
      if (isEdit.value) {
        loadProduct(route.params.id)
      }
    })

    return {
      isEdit,
      loading,
      submitting,
      submitError,
      successMessage,
      currentUserId,
      globalProductMatch,
      form,
      errors,
      isFormValid,
      validateBarcode,
      validateSku,
      validateName,
      validateDescription,
      validatePrice,
      validateStockQuantity,
      handleSubmit,
      hasUnsavedChanges,
      resetForm,
      useGlobalProduct,
      onNameChange,
      onBarcodeChange,
      onSkuGenerated,
      onSkuValidationError,
      // Scanner functionality
      scannerActive,
      scannerError,
      toggleBarcodeScanner,
      initBarcodeScanner,
      closeBarcodeScanner,
      onBarcodeDetected,
      onScannerError,
      onCameraReady
    }
  }
}
</script>

<style scoped>
.product-form {
  max-width: 600px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.loading-state {
  text-align: center;
  padding: 3rem;
}

.product-form-content {
  max-width: 100%;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2d3748;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.875rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.form-group input.error,
.form-group textarea.error {
  border-color: #e53e3e;
  box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-prefix {
  position: absolute;
  left: 0.875rem;
  color: #718096;
  font-weight: 500;
  z-index: 1;
}

.input-group input {
  padding-left: 2.5rem;
}

.field-help {
  color: #718096;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.error-message {
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

/* Global Product Match Styles */
.global-product-match {
  margin-top: 0.75rem;
  padding: 1rem;
  background: #e6fffa;
  border: 1px solid #81e6d9;
  border-radius: 8px;
}

.match-info h4 {
  margin: 0 0 0.5rem 0;
  color: #234e52;
  font-size: 0.875rem;
}

.match-info p {
  margin: 0.25rem 0;
  font-size: 0.875rem;
  color: #2d3748;
}

.status-valid {
  color: #276749;
  font-weight: 600;
}

.status-review {
  color: #d69e2e;
  font-weight: 600;
}

.btn-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.error-summary {
  background: #fed7d7;
  border: 1px solid #fc8181;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.error-summary h4 {
  color: #c53030;
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
}

.error-summary p {
  color: #e53e3e;
  margin: 0;
  font-size: 0.875rem;
}

.success-summary {
  background: #c6f6d5;
  border: 1px solid #68d391;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.success-summary h4 {
  color: #2f855a;
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
}

.success-summary p {
  color: #276749;
  margin: 0;
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  text-decoration: none;
}

.btn-secondary:hover {
  background: #5a6268;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn:disabled:hover {
  transform: none;
  box-shadow: none;
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

.btn-outline:disabled {
  color: #a0aec0;
  border-color: #a0aec0;
}

.btn-outline:disabled:hover {
  background: transparent;
  color: #a0aec0;
}

/* Barcode Scanner Styles */
.barcode-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.barcode-input-group input {
  flex: 1;
}

.btn-scan-barcode {
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 80px;
}

.btn-scan-barcode:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

.btn-scan-barcode:disabled {
  background: #a0aec0;
  cursor: not-allowed;
  transform: none;
}

.barcode-scanner-container {
  margin-top: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.scanner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.scanner-header h3 {
  margin: 0;
  color: #2d3748;
  font-size: 1.125rem;
}

.btn-close-scanner {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #718096;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close-scanner:hover {
  background: #e2e8f0;
  color: #2d3748;
}

.scanner-content {
  padding: 1rem;
}

.scanner-error {
  text-align: center;
  padding: 2rem;
  color: #e53e3e;
}

.scanner-error p {
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.camera-viewport {
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  background: #1a202c;
}

.barcode-reader {
  width: 100%;
  height: 300px;
  object-fit: cover;
}

.scanner-instructions {
  margin-top: 1rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.scanner-instructions p {
  margin: 0.5rem 0;
  font-size: 0.875rem;
  color: #4a5568;
}

.scanner-instructions p:first-child {
  margin-top: 0;
}

.scanner-instructions p:last-child {
  margin-bottom: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .product-form {
    padding: 0 1rem;
  }
  
  .header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .barcode-input-group {
    flex-direction: column;
  }
  
  .btn-scan-barcode {
    width: 100%;
    min-width: auto;
  }
  
  .scanner-header {
    padding: 0.75rem;
  }
  
  .scanner-content {
    padding: 0.75rem;
  }
  
  .barcode-reader {
    height: 250px;
  }
}
</style> 